import { resolverCanalEnConfig } from "./canalUtils";
import { toLocalISOString } from "../../utils/horaLocal";

export const CAMPOS_IMPORTACION_LEADS = [
  { key: "nombre", label: "Nombre", obligatorio: true, aliases: ["cliente", "nombre completo"] },
  { key: "telefono", label: "Teléfono", obligatorio: true, aliases: ["tel", "celular", "whatsapp"] },
  { key: "cursoInteres", label: "Producto/Servicio", aliases: ["producto", "servicio", "curso", "curso interes", "interes"] },
  { key: "montoEstimado", label: "Monto estimado", aliases: ["monto", "valor estimado", "oportunidad", "monto estimado de venta", "monto estimado de la venta"] },
  { key: "canal", label: "Canal" },
  { key: "observaciones", label: "Notas", aliases: ["observaciones", "observacion"] },
  { key: "edad", label: "Edad" },
  { key: "codigoPais", label: "Código país", aliases: ["codigo de pais", "prefijo"] },
  { key: "zona", label: "Zona" },
  { key: "etapa", label: "Etapa" },
  { key: "vendedorId", label: "Vendedor", aliases: ["responsable", "vendedor id"] },
  { key: "fechaContacto", label: "Fecha de contacto", aliases: ["fecha contacto"] },
  { key: "proximoSeguimiento", label: "Próximo seguimiento", aliases: ["fecha de seguimiento"] },
  { key: "lat", label: "Latitud" },
  { key: "lng", label: "Longitud" },
];
export const limpiarEncabezado = value => String(value ?? "").replace(/^\uFEFF/, "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\((obligatorio|opcional)\)/g, "").replace(/[_\s]+/g, " ").trim();
const text = value => String(value ?? "").trim();

export function parseCSV(input) {
  // Detect Excel's regional delimiter outside quoted cells in the header.
  const first = input.replace(/^\uFEFF/, "").split(/\r?\n/)[0];
  const counts = { ",": 0, ";": 0, "\t": 0 }; let quoted = false;
  for (const char of first) { if (char === '"') quoted = !quoted; else if (!quoted && char in counts) counts[char]++; }
  const separator = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  const rows = []; let row = [], cell = ""; quoted = false;
  for (let i = 0; i < input.length; i++) {
    const char = input[i], next = input[i + 1];
    if (char === '"' && quoted && next === '"') { cell += '"'; i++; }
    else if (char === '"') quoted = !quoted;
    else if (char === separator && !quoted) { row.push(cell.trim()); cell = ""; }
    else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") i++;
      row.push(cell.trim()); if (row.some(Boolean)) rows.push(row); row = []; cell = "";
    } else cell += char;
  }
  if (quoted) throw new Error("Hay una celda con comillas sin cerrar en el archivo CSV.");
  if (cell || row.length) { row.push(cell.trim()); if (row.some(Boolean)) rows.push(row); }
  return rows;
}
export function parseMonto(value) {
  if (typeof value === "number") return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) / 100 : null;
  let s = text(value).replace(/^(?:BOB|Bs\.?|USD|\$)\s*/i, "").replace(/\s/g, "");
  if (!s || !/^[0-9.,]+$/.test(s)) return null;
  const comma = s.lastIndexOf(","), dot = s.lastIndexOf(".");
  if (comma >= 0 && dot >= 0) { const decimal = comma > dot ? "," : "."; s = s.split(decimal === "," ? "." : ",").join("").replace(decimal, "."); }
  else if (comma >= 0 || dot >= 0) {
    const separator = comma >= 0 ? "," : ".";
    if (new RegExp("^\\d{1,3}(\\" + separator + "\\d{3})+$").test(s)) s = s.split(separator).join("");
    else if (s.split(separator).length === 2 && s.split(separator)[1].length <= 2) s = s.replace(separator, ".");
    else return null;
  }
  const n = Number(s); return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
}
function fechaImportada(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return toLocalISOString(value);
  const s = text(value); const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/); const local = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!iso && !local) return null;
  const y = Number(iso ? iso[1] : local[3]), m = Number(iso ? iso[2] : local[2]), d = Number(iso ? iso[3] : local[1]);
  const date = new Date(y, m - 1, d); if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return toLocalISOString(date).slice(0, 10);
}
export function prepararFilasImportacion(filas, config) {
  const encabezados = (filas[0] || []).map(limpiarEncabezado);
  const indices = Object.fromEntries(CAMPOS_IMPORTACION_LEADS.map(c => [c.key, encabezados.findIndex(h => [c.key, c.label, ...(c.aliases || [])].map(limpiarEncabezado).includes(h))]));
  const faltantes = CAMPOS_IMPORTACION_LEADS.filter(c => c.obligatorio && indices[c.key] < 0);
  if (faltantes.length) throw new Error("Faltan columnas obligatorias: " + faltantes.map(c => c.label).join(", "));
  const conocidas = new Set(Object.values(indices));
  const noReconocidas = encabezados.filter((h, i) => h && !conocidas.has(i));
  const erroresValidacion = [], filasValidas = [];
  for (let index = 1; index < filas.length; index++) {
    const fila = filas[index]; if (!fila.some(v => text(v))) continue;
    const lead = {}, errors = []; const numeroFila = index + 1;
    for (const campo of CAMPOS_IMPORTACION_LEADS) if (indices[campo.key] >= 0) lead[campo.key] = fila[indices[campo.key]] ?? "";
    const camposImportados = CAMPOS_IMPORTACION_LEADS.filter(c => indices[c.key] >= 0 && text(lead[c.key])).map(c => c.key);
    for (const campo of CAMPOS_IMPORTACION_LEADS.filter(c => c.obligatorio)) if (!text(lead[campo.key])) errors.push("falta " + campo.label);
    for (const key of ["nombre", "telefono", "cursoInteres", "observaciones", "zona", "codigoPais"]) if (key in lead) lead[key] = text(lead[key]);
    if (text(lead.montoEstimado)) { const monto = parseMonto(lead.montoEstimado); if (monto === null) errors.push("Monto estimado inválido"); else lead.montoEstimado = monto; } else lead.montoEstimado = null;
    if (text(lead.edad)) { const edad = Number(lead.edad); if (!Number.isInteger(edad) || edad < 0 || edad > 130) errors.push("Edad inválida"); else lead.edad = String(edad); }
    lead.codigoPais = lead.codigoPais || config.codigoPais || "591";
    if (!/^\+?\d{1,4}$/.test(lead.codigoPais)) errors.push("Código país inválido"); else lead.codigoPais = lead.codigoPais.replace(/^\+/, "");
    if (text(lead.canal)) lead.canal = resolverCanalEnConfig(text(lead.canal), config.canales || []); else lead.canal = "whatsapp";
    if (text(lead.etapa)) { const etapa = (config.etapasCatalogo || []).find(e => [e.id, e.nombre].some(v => limpiarEncabezado(v) === limpiarEncabezado(lead.etapa))); if (!etapa) errors.push("Etapa no configurada"); else lead.etapa = etapa.id; }
    else lead.etapa = config.etapasPipeline?.includes("nuevo") ? "nuevo" : config.etapasPipeline?.[0] || "nuevo";
    if (text(lead.vendedorId)) { const sellers = (config.vendedores || []).filter(v => v.activo !== false && [v.id, v.nombre].some(n => limpiarEncabezado(n) === limpiarEncabezado(lead.vendedorId))); if (sellers.length !== 1) errors.push("Vendedor no configurado o ambiguo"); else lead.vendedorId = sellers[0].id; } else lead.vendedorId = null;
    for (const key of ["fechaContacto", "proximoSeguimiento"]) if (text(lead[key])) { const fecha = fechaImportada(lead[key]); if (!fecha) errors.push(key + " inválida (usa AAAA-MM-DD o DD/MM/AAAA)"); else lead[key] = fecha; } else delete lead[key];
    const lat = text(lead.lat), lng = text(lead.lng);
    if (lat || lng) { const a = Number(lat.replace(",", ".")), b = Number(lng.replace(",", ".")); if (!lat || !lng || !Number.isFinite(a) || !Number.isFinite(b) || Math.abs(a) > 90 || Math.abs(b) > 180) errors.push("Ubicación inválida"); else lead.location = { lat: a, lng: b }; }
    if (lead.location) camposImportados.push("location");
    delete lead.lat; delete lead.lng;
    if (errors.length) erroresValidacion.push("Fila " + numeroFila + ": " + errors.join(", ")); else filasValidas.push({ lead, numeroFila, camposImportados: camposImportados.filter(k => k !== "lat" && k !== "lng") });
  }
  return { filasValidas, erroresValidacion, noReconocidas };
}
export async function leerFilasArchivo(archivo) {
  if (/\.csv$/i.test(archivo.name)) return parseCSV(await archivo.text());
  if (!/\.xlsx?$/i.test(archivo.name)) throw new Error("Selecciona un archivo CSV, XLSX o XLS.");
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await archivo.arrayBuffer(), { type: "array", cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet || !sheet["!ref"]) return [];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true, blankrows: true });
  const headers = rows[0]?.map(limpiarEncabezado) || [];
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  for (const campo of CAMPOS_IMPORTACION_LEADS.filter(c => ["telefono", "codigoPais"].includes(c.key))) {
    const col = headers.findIndex(h => [campo.key, campo.label, ...(campo.aliases || [])].map(limpiarEncabezado).includes(h));
    if (col < 0) continue;
    for (let row = 1; row < rows.length; row++) {
      const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r + row, c: range.s.c + col })];
      if (cell?.t === "n") rows[row][col] = cell.w && !/[eE][+-]?\d+/.test(cell.w) ? cell.w : String(cell.v);
    }
  }
  return rows;
}
